import { useEffect, useState } from 'react';
import { X, Send, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { gastosApi } from '../../utils/api';

const fechaCR = (d) => d ? String(d).slice(0, 10).split('-').reverse().join('/') : '';
const esCorreo = (c) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c);

/** Lista de correos editable (pastillas con quitar + cuadro para agregar) */
function Correos({ etiqueta, lista, setLista, origen = {} }) {
  const [nuevo, setNuevo] = useState('');
  const agregar = () => {
    const c = nuevo.trim();
    if (!c) return;
    if (!esCorreo(c)) { toast.error('Ese correo no es válido'); return; }
    if (!lista.includes(c)) setLista([...lista, c]);
    setNuevo('');
  };
  return (
    <div>
      <div className="text-sm font-medium text-gray-700 mb-1">{etiqueta}</div>
      <div className="flex flex-wrap gap-1.5 items-center">
        {lista.map(c => (
          <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-300 text-xs text-gray-700">
            {c}{origen[c] && <span className="text-gray-400">· {origen[c]}</span>}
            <button type="button" onClick={() => setLista(lista.filter(x => x !== c))} className="text-gray-400 hover:text-red-600"><X size={12} /></button>
          </span>
        ))}
        <input value={nuevo} onChange={e => setNuevo(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); agregar(); } }} onBlur={agregar}
          placeholder="+ agregar correo" className="min-w-[10rem] flex-1 border border-gray-200 rounded-md px-2 py-0.5 text-xs" />
      </div>
    </div>
  );
}

/**
 * Correo al proveedor de una factura que no se acepta: destinatarios (sus contactos registrados) y el correo
 * tal como lo recibe. modo 'pasar': desde «Por Confirmar» (pasa la factura a pendientes, con o sin correo);
 * modo 'enviar': desde «Pendientes de rechazo» (enviar o reenviar).
 */
export default function CorreoProveedorModal({ facturaId, pendienteId, modo = 'enviar', onClose, onListo }) {
  const [c, setC] = useState(null);
  const [para, setPara] = useState([]);
  const [cc, setCc] = useState([]);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    gastosApi.getCorreoProveedor({ facturaId, pendienteId })
      .then(r => { const d = r.data?.data; setC(d); setPara(d?.para || []); setCc(d?.cc || []); })
      .catch(err => { (!err.response && toast.error('No se pudo armar el correo')); onClose(); });
  }, [facturaId, pendienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const accion = async (enviar) => {
    if (enviar && !para.length) { toast.error('Agregue al menos un correo del proveedor'); return; }
    setEnviando(true);
    try {
      if (modo === 'pasar') {
        const r = await gastosApi.pasarAPendientes({ facturaId, enviarCorreo: enviar, para, cc });
        if (r.data?.data?.correoError) toast.error(`Pasó a pendientes, pero el correo no salió: ${r.data.data.correoError}`);
        else toast.success(enviar ? 'Pasó a pendientes de rechazo y se envió el correo' : 'Pasó a pendientes de rechazo');
      } else {
        await gastosApi.enviarCorreoPendiente(pendienteId, { para, cc });
        toast.success('Correo enviado al proveedor');
      }
      onListo?.();
    } catch (err) {
      (!err.response && toast.error('No se pudo completar'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-6xl border-2 border-gray-200 max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{modo === 'pasar' ? 'Pasar a pendientes de rechazo' : 'Correo al proveedor'}</h3>
            {c?.vence && <p className="text-sm text-gray-500">Responder a Hacienda a más tardar el <strong>{fechaCR(c.vence)}</strong></p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {!c ? <div className="py-10 text-center text-sm text-gray-500">Armando el correo...</div> : (
          <div className="grid grid-cols-1 lg:grid-cols-5 min-h-0 flex-1">
            <div className="lg:col-span-2 p-5 space-y-4 border-b lg:border-b-0 lg:border-r overflow-y-auto">
              <Correos etiqueta="Para (contactos del proveedor)" lista={para} setLista={setPara} origen={c.origen} />
              <Correos etiqueta="Copia" lista={cc} setLista={setCc} />
              {!para.length && <p className="text-xs text-amber-700">El proveedor no tiene correo registrado. Escriba uno arriba.</p>}
              <p className="text-xs text-gray-500">El texto sale de la plantilla de la empresa (Configuración → Facturas recibidas).</p>
            </div>
            <div className="lg:col-span-3 p-5 flex flex-col min-h-0 bg-gray-50">
              <div className="text-sm mb-2"><span className="text-gray-500">Asunto:</span> <span className="font-medium">{c.asunto}</span></div>
              <iframe key={c.html.length} title="Correo" sandbox="" srcDoc={c.html} className="w-full flex-1 min-h-[440px] border rounded-lg bg-white" />
            </div>
          </div>
        )}
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">Cancelar</button>
          {modo === 'pasar' && (
            <button disabled={!c || enviando} onClick={() => accion(false)} className="flex items-center gap-2 px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50">
              <Check size={16} /> Pasar sin enviar correo
            </button>
          )}
          <button disabled={!c || enviando} onClick={() => accion(true)} className="flex items-center gap-2 px-4 py-2 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50">
            <Send size={16} /> {modo === 'pasar' ? 'Pasar y enviar correo' : 'Enviar correo'}
          </button>
        </div>
      </div>
    </div>
  );
}
